import { Component } from 'preact-compat';
import PropTypes from 'prop-types';
import * as THREE from '../../three.min.js';
import s from './scss/three.scss';

export default class Three extends Component {
  static propTypes = {
    imgPath: PropTypes.string.isRequired,
    stageWidth: PropTypes.number.isRequired,
    stageHeight: PropTypes.number.isRequired,
    loadHandling: PropTypes.func.isRequired,
    removeLayer: PropTypes.func.isRequired,
    absPath: PropTypes.string.isRequired,
    thumbbarHover: PropTypes.bool,
    thumbbarOpen: PropTypes.bool,
    interior: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.onWindowResize = this.onWindowResize.bind(this);
    this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this);
    this.onDocumentMouseUp = this.onDocumentMouseUp.bind(this);
    this.onDocumentMouseMove = this.onDocumentMouseMove.bind(this);
    this.onDocumentTouchStart = this.onDocumentTouchStart.bind(this);
    this.onDocumentTouchMove = this.onDocumentTouchMove.bind(this);

    this.fovMin = 30;
    this.fovMax = 60;
  }

  componentDidUpdate(prevProps) {
    const { stageHeight, stageWidth, interior } = this.props;
    if (
      prevProps.stageWidth !== stageWidth ||
      prevProps.stageHeight !== stageHeight
    ) {
      this.onWindowResize();
    } else if (
      interior &&
      prevProps.interior.config[prevProps.interior.index] !==
        interior.config[interior.index]
    ) {
      this.stop();
      this.mount.removeChild(this.renderer.domElement);
      this.frameId = undefined;
      this.init();
    }

    if (prevProps.zoomFactor !== this.props.zoomFactor) {
      if (this.props.zoomFactor !== 0) {
        this.zoomIn = this.props.zoomFactor > 0;
        // change zoomTo to change steps of zoomFactor
        this.zoomAnimating = true;
        this.zoomTo = this.camera.fov + this.props.zoomFactor;
      }
    }
  }
  // componentWi
  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize, false);
    document.addEventListener('mousedown', this.onDocumentMouseDown, false);
    document.addEventListener('mousemove', this.onDocumentMouseMove, false);
    document.addEventListener('mouseup', this.onDocumentMouseUp, false);
    document.addEventListener('touchstart', this.onDocumentTouchStart, false);
    document.addEventListener('touchmove', this.onDocumentTouchMove, {passive: false});
    // document.addEventListener('mousedown', this.onPointerStart, false);
    // document.addEventListener('mousemove', this.onPointerMove, false);
    // document.addEventListener('mouseup', this.onPointerUp, false);
    // document.addEventListener('touchstart', this.onPointerStart, false);
    // document.addEventListener('touchmove', this.onPointerMove, false);
    // document.addEventListener('touchend', this.onPointerUp, false);

    this.init();
  }

  init = () => {
    const { absPath, interior, imgPath } = this.props;
    // set init values for controls and positions
    const isUserInteracting = false;
    const onMouseDownMouseX = 0;
    const onMouseDownMouseY = 0;
    const lon = 180;
    const onMouseDownLon = 0;
    const lat = -11;
    const onMouseDownLat = 0;
    const phi = 0;
    const theta = 0;
    const target = new THREE.Vector3();
    const loaded = 0;
    const devicePixelRatio = window.devicePixelRatio || 1;
    const logicalViewportWidth = window.innerWidth;
    const physicalViewportWidth = logicalViewportWidth * devicePixelRatio;
    const pauseTimer = null;
    const pauseTime = 5000;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, 2.1, 0.8, 1100);
    // set init target
    target.x = 5;
    target.y = 5;
    target.z = 5;
    camera.lookAt(target);

    const code = interior ? interior.config[interior.index].code : 'image';
    const version = interior
      ? `_${interior.config[interior.index].version}`
      : '';

    const noIndex = imgPath.replace('index.html', '');

    const correctImgPath =
        process.env.NODE_ENV === 'production' ? noIndex : imgPath;

      const urls = [
      absPath +
        `assets/images/${correctImgPath}int/${code}_f${version}.jpg?imwidth=${physicalViewportWidth}`,
      absPath +
        `assets/images/${correctImgPath}int/${code}_b${version}.jpg?imwidth=${physicalViewportWidth}`,
      absPath +
        `assets/images/${correctImgPath}int/${code}_u${version}.jpg?imwidth=${physicalViewportWidth}`,
      absPath +
        `assets/images/${correctImgPath}int/${code}_d${version}.jpg?imwidth=${physicalViewportWidth}`,
      absPath +
        `assets/images/${correctImgPath}int/${code}_l${version}.jpg?imwidth=${physicalViewportWidth}`,
      absPath +
        `assets/images/${correctImgPath}int/${code}_r${version}.jpg?imwidth=${physicalViewportWidth}`
    ];

    const geometry = new THREE.BoxGeometry(300, 300, 300, 7, 7, 7);
    // load textures
    const textures = [
      new THREE.TextureLoader().load(
        urls[0],
        this.loadImage,
        undefined,
        this.loadImage
      ),
      new THREE.TextureLoader().load(
        urls[1],
        this.loadImage,
        undefined,
        this.loadImage
      ),
      new THREE.TextureLoader().load(
        urls[2],
        this.loadImage,
        undefined,
        this.loadImage
      ),
      new THREE.TextureLoader().load(
        urls[3],
        this.loadImage,
        undefined,
        this.loadImage
      ),
      new THREE.TextureLoader().load(
        urls[4],
        this.loadImage,
        undefined,
        this.loadImage
      ),
      new THREE.TextureLoader().load(
        urls[5],
        this.loadImage,
        undefined,
        this.loadImage
      )
    ];

    // add textures to mesh
    const material = [
      new THREE.MeshBasicMaterial({ map: textures[0] }),
      new THREE.MeshBasicMaterial({ map: textures[1] }),
      new THREE.MeshBasicMaterial({ map: textures[2] }),
      new THREE.MeshBasicMaterial({ map: textures[3] }),
      new THREE.MeshBasicMaterial({ map: textures[4] }),
      new THREE.MeshBasicMaterial({ map: textures[5] })
    ];

    // combine texture and cube
    geometry.scale(-1, 1, 1);
    const mesh = new THREE.Mesh(geometry, material);

    scene.add(mesh);

    const renderer = new THREE.WebGLRenderer();
    renderer.setClearColor(0xffffff, 1);
    renderer.setPixelRatio(window.devicePixelRatio);
    camera.aspect = this.props.stageWidth / this.props.stageHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(this.props.stageWidth, this.props.stageHeight);

    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.material = material;
    this.isUserInteracting = isUserInteracting;
    this.onMouseDownMouseX = onMouseDownMouseX;
    this.onMouseDownMouseY = onMouseDownMouseY;
    this.lon = lon;
    this.onMouseDownLon = onMouseDownLon;
    this.lat = lat;
    this.onMouseDownLat = onMouseDownLat;
    this.phi = phi;
    this.theta = theta;
    this.target = target;
    this.loaded = loaded;
    this.pauseTimer = pauseTimer;
    this.pauseTime = pauseTime;
    this.isPaused = false;

    this.mount.appendChild(this.renderer.domElement);
    this.start();
    this.pause();
  };

  // callback loadHandling
  loadImage = () => {
    this.props.loadHandling(6, true);
  };

  componentWillUnmount() {
    this.stop();
    this.mount.removeChild(this.renderer.domElement);

    window.removeEventListener('resize', this.onWindowResize, false);
    document.removeEventListener('mousedown', this.onDocumentMouseDown, false);
    document.removeEventListener('mousemove', this.onDocumentMouseMove, false);
    document.removeEventListener('mouseup', this.onDocumentMouseUp, false);
    document.removeEventListener('touchstart', this.onDocumentTouchStart, false);
    document.removeEventListener('touchmove', this.onDocumentTouchMove, {passive: false});
    // document.removeEventListener('mousedown', this.onPointerStart, false);
    // document.removeEventListener('mousemove', this.onPointerMove, false);
    // document.removeEventListener('mouseup', this.onPointerUp, false);
    // document.removeEventListener('touchstart', this.onPointerStart, false);
    // document.removeEventListener('touchmove', this.onPointerMove, false);
    // document.removeEventListener('touchend', this.onPointerUp, false);
  }

  onPointerStart = event => {
    this.isUserInteracting = true;
    this.pause();
    const clientX = (event.touches && event.touches.length >= 1
      ? event.touches[0]
      : event
    ).clientX;
    const clientY = (event.touches && event.touches.length >= 1
      ? event.touches[0]
      : event
    ).clientY;
    this.onMouseDownMouseX = clientX;
    this.onMouseDownMouseY = clientY;
    this.onMouseDownLon = this.lon;
    this.onMouseDownLat = this.lat;
  };

  onPointerMove = event => {
    if (this.isUserInteracting === true) {
      const clientX = (event.touches && event.touches.length >= 1
        ? event.touches[0]
        : event
      ).clientX;
      const clientY = (event.touches && event.touches.length >= 1
        ? event.touches[0]
        : event
      ).clientY;
      this.lon = (this.onMouseDownMouseX - clientX) * 0.1 + this.onMouseDownLon;
      this.lat = (clientY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
    }
  };

  onPointerUp = () => {
    this.isUserInteracting = false;
    this.pause();
  };

  onDocumentMouseDown(event) {
    this.props.removeLayer();

    event.preventDefault();
    this.isUserInteracting = true;
    this.onPointerDownPointerX = event.clientX;
    this.onPointerDownPointerY = event.clientY;
    this.onPointerDownLon = this.lon;
    this.onPointerDownLat = this.lat;
  }

  onDocumentMouseMove(event) {
    const { thumbbarHover, thumbbarOpen } = this.props;

    if (!thumbbarHover || !thumbbarOpen) {
      if (this.isUserInteracting) {
        this.lon = (this.onPointerDownPointerX - event.clientX) * 0.1 + this.onPointerDownLon;

        this.lat = (event.clientY - this.onPointerDownPointerY) * 0.1 + this.onPointerDownLat;
      }
    }
  }

  onDocumentMouseUp() {
    this.isUserInteracting = false;
  }

  onDocumentTouchStart(event) {
    this.props.removeLayer();

    if (event.touches.length === 1) {
      this.onPointerDownPointerX = event.touches[0].pageX;
      this.onPointerDownPointerY = event.touches[0].pageY;
      this.onPointerDownLon = this.lon;
      this.onPointerDownLat = this.lat;
    }

    if (event.touches.length === 2) {
      this.onPointerDownPointerX = event.touches[0].pageX;
      this.onPointerDownPointerY = event.touches[0].pageY;

      this.startDistance = this.endDistance = Math.hypot(
  event.touches[0].pageX - event.touches[1].pageX,
        event.touches[0].pageY - event.touches[1].pageY
      );
    }
  }

  onDocumentTouchMove(event) {
    const { thumbbarHover, thumbbarOpen } = this.props;

    event.preventDefault();

    if (!thumbbarHover || !thumbbarOpen) {
      if (event.touches.length === 1) {
        this.lon = (this.onPointerDownPointerX - event.touches[0].pageX) * 0.1 + this.onPointerDownLon;

        this.lat = (event.touches[0].pageY - this.onPointerDownPointerY) * 0.1 + this.onPointerDownLat;
      }
    }
  }

  pause = () => {
    this.isPaused = true;

    /* START disable auto-rotate */
    // if (this.pauseTimer) {
    //   clearTimeout(this.pauseTimer);
    // }
    //
    // this.pauseTimer = setTimeout(() => {
    //   this.isPaused = false;
    // }, this.pauseTime);
    /* END disable auto-rotate */
  };

  start = () => {
    if (!this.frameId) {
      this.frameId = requestAnimationFrame(this.animate);
    }
  };

  stop = () => {
    cancelAnimationFrame(this.frameId);
  };

  animate = () => {
    this.update();

    if (this.zoomAnimating) {
      this.clickZoom();
    }
    this.frameId = window.requestAnimationFrame(this.animate);
  };

  onWindowResize = () => {
    this.camera.aspect = this.props.stageWidth / this.props.stageHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.props.stageWidth, this.props.stageHeight);
  };

  update = () => {
    const { thumbbarHover, thumbbarOpen } = this.props;
    if (!thumbbarHover || !thumbbarOpen || !this.isUserInteracting) {
        if (this.isPaused === false) {
            this.lon += 0.1;
            if (this.lat > 0) {
                this.lat -= 0.1;
            } else if (this.lat < 0) {
                this.lat += 0.1;
            }
        }
    }
    this.lat = Math.max(-85, Math.min(85, this.lat));
    this.phi = THREE.MathUtils.degToRad(90 - this.lat);
    this.theta = THREE.MathUtils.degToRad(this.lon);
    this.target.x = 500 * Math.sin(this.phi) * Math.cos(this.theta);
    this.target.y = 500 * Math.cos(this.phi);
    this.target.z = 500 * Math.sin(this.phi) * Math.sin(this.theta);
    this.camera.lookAt(this.target);
    this.renderer.render(this.scene, this.camera);
  };

  clickZoom = () => {
    let fov;
    // change zoomSpeed for faster/slower zooming
    const zoomSpeed = this.zoomIn ? -1 : +1;
    if (
      this.camera.fov !== this.zoomTo &&
      (this.camera.fov >= this.fovMin && this.camera.fov <= this.fovMax)
    ) {
      fov = this.camera.fov + zoomSpeed * 0.5;
      this.camera.fov = THREE.MathUtils.clamp(fov, this.fovMin, this.fovMax);
      this.camera.updateProjectionMatrix();
    } else {
      this.zoomTo = this.camera.fov;
      this.zoomAnimating = false;
      this.props.zoomStopped();
    }
  };

  render() {
    return (
      <div
        className={s.interior}
        style={{ width: '100%', height: '100%' }}
        ref={mount => {
          this.mount = mount;
        }}
      />
    );
  }
}
